import { useEffect, useRef, memo, useCallback } from 'react'
import {
  alertColors,
  alertLabels,
  alertTextColors,
} from '~/packages/general/variable/spaceTime.data'
import { HeadSection, InfoSection } from '~/components/mapContent/styles/modalSelectVariable'
import { WrapperTags } from '~/components/mapContent/styles/modalSelectVariable'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import { VariableSizeList as List } from 'react-window'
import Loader from '~/prix/react/components/loader'
import { Expression, ExpressionType, ShapeBase } from '~/prix/types/expression'
import ExpressionsContainer from './expressionContainer'
import { ZodSchemaInfo } from '~/prix/types/zodType'
import { Left, Right, Title } from '../../styles/modals'
import { ViewListItem } from '~/packages/general/view/list/viewList.typing'
import VariableSelectionDetails from './selectionDetails'

interface ScrollListItemProps {
  index: number
  style: React.CSSProperties
  isItemLoaded: (index: number) => boolean
  variables: ViewListItem[]
  currentBaseLayer: Layer
  handleSelectVariable: (variable: ViewListItem) => void
  listRef: React.RefObject<List>
  sizes: number[]
  isApiLoading: boolean
  getFilter: (expression: Record<number, Expression> | undefined, index: string) => void
  filters: Expression[] | undefined
  selectedVariableIsFilterableBy: ZodSchemaInfo | undefined
}

const ScrollListItem = ({
  index,
  style,
  isItemLoaded,
  variables,
  currentBaseLayer,
  handleSelectVariable,
  listRef,
  sizes,
  isApiLoading,
  getFilter,
  filters,
  selectedVariableIsFilterableBy,
}: ScrollListItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null)
  const infoRef = useRef<HTMLDivElement>(null)

  const updateSize = useCallback(() => {
    if (itemRef.current) {
      const height = itemRef.current.offsetHeight
      if (height !== sizes[index]) {
        sizes[index] = height
        if (listRef.current) {
          listRef.current.resetAfterIndex(index)
        }
      }
    }
  }, [index, sizes, listRef])
  useEffect(() => {
    updateSize()
  }, [updateSize])

  if (!isItemLoaded(index)) {
    return (
      <div style={style}>
        <Loader />
      </div>
    )
  }

  const content = variables[index]

  const isSelected =
    currentBaseLayer.variable.key === content?.key &&
    currentBaseLayer.variable.presetKey === content?.presetKey

  return (
    <div style={style}>
      <article
        className={`variable ${isSelected ? 'is-selected' : ''}`}
        role='button'
        onClick={() => {
          handleSelectVariable(content)
        }}
        ref={itemRef}
      >
        <HeadSection>
          <Left>
            <Title>{content.label ? content.label : content.title}</Title>
            <small>{content?.label ? content?.title : null}</small>
          </Left>
          <Right>
            {content.result?.alert ? (
              <small
                className='alert'
                style={{
                  backgroundColor: alertColors[content.result?.alert],
                  color: alertTextColors[content.result?.alert],
                }}
              >
                {alertLabels[content.result?.alert]}
              </small>
            ) : null}
            {/* {content.key === 'attendanceOccurrence' || content.key === 'prescribedDrugs' ? (
                <ButtonVariants
                  onClick={e => {
                    e.stopPropagation()
                    getCollapsedOpen(
                      !collapsedStates[`${content.title}-${content?.label}` as string],
                      `${content.title}-${content?.label}`,
                    )
                  }}
                  icon={
                    collapsedStates[`${content.title}-${content?.label}` as string]
                      ? faChevronUp
                      : faChevronDown
                  }
                  variant='transparent'
                  size='sm'
                />
              ) : null} */}
          </Right>
        </HeadSection>
        <WrapperTags>
          {content?.warnings?.length ? (
            <small className='attention'>
              {content?.warnings.map((label, index) => [
                '⚠️  ',
                label,
                index < content?.warnings!.length - 1 ? <br /> : null,
              ])}
            </small>
          ) : null}
        </WrapperTags>
        <InfoSection ref={infoRef}>
          {isSelected && (
            <div onClick={e => e.stopPropagation()}>
              {selectedVariableIsFilterableBy && (
                <ExpressionsContainer
                  getFilter={getFilter}
                  filtersApplied={filters}
                  isLoading={isApiLoading}
                  filtersAvailable={
                    content?.filterableBy as ExpressionType<Expression<string>, ShapeBase> | undefined
                  }
                  selectedVariableIsFilterableBy={selectedVariableIsFilterableBy}
                  resizeHeight={updateSize}
                />
              )}
              <VariableSelectionDetails variable={content} />
            </div>
          )}
        </InfoSection>
      </article>
    </div>
  )
}

export default memo(ScrollListItem)
